/**
 * yearAxis (#1934) — pure helpers behind the /farm Erntejahr navigation.
 *
 * The ordering + label + sentinel semantics are COPIED from
 * ``apps/deckungsbeitrag/deckungsbeitragRollup.ts`` (``periodsOf`` /
 * ``periodLabel`` / ``OHNE_JAHR``) rather than imported: /farm year nav is a
 * list-level facet axis while DeckungsbeitragPage is a fact rollup, and
 * importing across the two would drag the WJ-label + fact-row types over an
 * app boundary and re-couple surfaces #1939 is deliberately untangling. The
 * three helpers are tiny and their contract is locked by tests on both sides.
 *
 * Input is the ``{value, count}`` facet from
 * ``GET /twin/entities/{type}/facets`` — a ``null`` value is the "Ohne Jahr"
 * bucket (rows missing / JSON-null on the year attribute), expressed
 * downstream as ``_missing=<year_attribute>`` (JSONB containment can't
 * express "key absent"). Their rows are real money, never dropped.
 */
import type { EntityFacetItem } from '../../services/twinService';

/**
 * Sentinel year id for the null/blank bucket — copied from
 * ``deckungsbeitragRollup.OHNE_JAHR`` (kept byte-identical so a ``?year=``
 * value reads the same across the Deckungsbeitrag and /farm surfaces).
 */
export const OHNE_JAHR = 'ohne-jahr';

export interface YearBucket {
  /** URL/id token: the raw year string, or ``OHNE_JAHR`` for the null bucket. */
  id: string;
  /** Human label — the raw year, or "Ohne Jahr" for the sentinel. */
  label: string;
  /** Absolute per-year row count from the facet response. */
  count: number;
  /** True for the null/blank bucket — routes the filter through ``_missing``. */
  isOhneJahr: boolean;
}

/**
 * Human label for a year id. Copied from ``deckungsbeitragRollup.periodLabel``
 * MINUS the Wirtschaftsjahr hint: the /farm axis shows plain Erntejahre, so it
 * needs neither ``wjStartMonth`` nor the ``liquiditaet_config`` dependency.
 * Null / the ``OHNE_JAHR`` sentinel → "Ohne Jahr"; any other value passes
 * through untouched (a non-4-digit label is a valid pass-through).
 */
export function yearLabel(year: string | null): string {
  if (year === null || year === OHNE_JAHR) return 'Ohne Jahr';
  return year;
}

/**
 * Facet items → ordered year buckets. Distinct named years newest-first, the
 * null bucket (as ``OHNE_JAHR``) always last — the ordering contract copied
 * from ``deckungsbeitragRollup.periodsOf``. The server already returns
 * ``value DESC NULLS LAST``, but we re-sort defensively (source-order
 * independence) and fold every null-ish item into the single Ohne-Jahr bucket
 * so a duplicate can't double-render the axis.
 */
export function yearBuckets(items: EntityFacetItem[]): YearBucket[] {
  const named: YearBucket[] = [];
  // ``null`` = no null-year rows seen yet; a number = the folded Ohne-Jahr
  // total (every null-ish item sums into the one bucket, built once below).
  let ohneJahrCount: number | null = null;
  for (const item of items) {
    if (item.value === null) {
      ohneJahrCount = (ohneJahrCount ?? 0) + item.count;
    } else {
      named.push({ id: item.value, label: yearLabel(item.value), count: item.count, isOhneJahr: false });
    }
  }
  named.sort((a, b) => b.id.localeCompare(a.id, 'de'));
  if (ohneJahrCount !== null) {
    named.push({ id: OHNE_JAHR, label: yearLabel(null), count: ohneJahrCount, isOhneJahr: true });
  }
  return named;
}

/**
 * Whether the year axis is worth surfacing as a control: at least TWO distinct
 * NAMED years. The ``OHNE_JAHR`` sentinel does NOT count (#1934 P3-2) — a facet
 * of one real year plus a null bucket (``{2024, null}``) is still a single-year
 * tenant, so the control stays hidden; ``{2023, 2024, null}`` shows it. Governs
 * control visibility only, never whether a ``?year=`` filter applies.
 */
export function hasYearAxis(buckets: YearBucket[]): boolean {
  return buckets.filter((b) => !b.isOhneJahr).length >= 2;
}

/**
 * Immutably fold a ``?year=`` value into an existing query string — the pure
 * write-path the /farm year control uses (mirrors
 * ``farm/object/asof.ts::withAsOfParam``). A truthy year sets the param, a
 * null/empty one clears it (back to all years). Kept pure so the URL round-trip
 * is unit-tested against the exact code the page runs.
 */
export function withYearParam(prev: URLSearchParams, year: string | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (year) next.set('year', year);
  else next.delete('year');
  return next;
}
